/**
 * The processing queue, read as an operations console rather than as a list of rows.
 *
 * The box that runs the pipeline is asleep most of the day and a mañanera takes hours to
 * transcribe, so for long stretches the only difference between "working" and "dead" is a stage
 * name in one field. Everything here exists to make that difference legible: which of the three
 * states the machine is in, how far each run actually got, how long it has been like that, and
 * whether a failure is worth asking for again.
 *
 * Pure, and `now` is a parameter — a module that reads the clock itself cannot be tested.
 */

import type { Tone } from 'src/lib/tone';
import { PIPELINE_STAGES, type PipelineJob, type PipelineStage } from 'src/types/editorial';

export type MachineState = 'working' | 'waking' | 'blocked' | 'idle';

/**
 * What the three states are called, in one place.
 *
 * Two screens report this now — the console and the rail — and they were calling the same state
 * `Apagada` and `En reposo`. An interface that has two words for one machine state is an interface
 * an editor has to translate, so the words live with the states rather than with either screen.
 */
export const MACHINE_COPY: Record<MachineState, { label: string; detail: string; tone: Tone }> = {
  working: {
    label: 'Procesando',
    detail: 'La máquina está encendida y trabajando.',
    tone: 'supported',
  },
  waking: {
    label: 'Encendiendo',
    detail: 'Hay trabajo en cola y la máquina está arrancando. Tarda unos minutos.',
    tone: 'partial',
  },
  blocked: {
    label: 'En espera',
    detail: 'Hay trabajo en cola que no puede correr todavía y se reintenta solo. La máquina no es el problema.',
    tone: 'partial',
  },
  idle: {
    label: 'Apagada',
    detail: 'Sin trabajo pendiente. Se enciende sola cuando pidas algo.',
    tone: 'unresolved',
  },
};

export type JobProgress = {
  /** Stages this run asked for, in order, each with its own standing. */
  track: Array<{ stage: PipelineStage; standing: 'done' | 'running' | 'pending' | 'abandoned' }>;
  done: number;
  total: number;
  /** 0–100, by stages completed. Deliberately not smoothed: a stage is done or it is not. */
  percent: number;
  current: PipelineStage | null;
};

export type JobView = {
  job: PipelineJob;
  progress: JobProgress;
  /** Milliseconds the run has been going, or took. Null when it never started. */
  runningMs: number | null;
  /** Milliseconds spent waiting to be claimed. Null once it started. */
  waitingMs: number | null;
  /** A failure worth resubmitting: the box died or the provider did, not a bad request. */
  retryable: boolean;
};

export type JobBoard = {
  machine: MachineState;
  running: JobView[];
  queued: JobView[];
  history: JobView[];
  throughput: {
    /** Settled in the last 24 hours — a rolling window, not a calendar day: a mañanera processed
     *  at 23:40 and one processed at 00:10 are the same shift's work. */
    finishedToday: number;
    failedToday: number;
    /** Median wall-clock of the runs that finished in the window, in ms. Null when none did. */
    medianMs: number | null;
  };
};

/**
 * What EC2 says the box is doing. `unknown` covers a lookup that failed and an older API that
 * does not report it — both mean "fall back to reading the queue".
 */
export type PipelineInstanceState =
  | 'pending'
  | 'running'
  | 'stopping'
  | 'stopped'
  | 'shutting-down'
  | 'terminated'
  | 'not_configured'
  | 'unknown';

/**
 * The machine state, preferring the machine's own answer.
 *
 * Inferring it from the queue was right while a cold box was the only reason work could sit
 * unclaimed. Once a job could wait on a provider or a timer, the same queue shape meant three
 * different things, and the panel reported "Encendiendo" over an instance that was switched off.
 *
 * The queue still decides between working, blocked and idle on a box that is up — EC2 knows the
 * instance is running, not whether anything is being processed on it.
 */
function machineStateFrom(instance: PipelineInstanceState, inferred: MachineState): MachineState {
  if (instance === 'pending') return 'waking';
  if (instance === 'stopped' || instance === 'stopping' || instance === 'shutting-down' || instance === 'terminated') {
    // Work queued against a box that is off is waiting on the box, which is what `waking` says —
    // submitting a job wakes it. With nothing queued the box is simply off.
    return inferred === 'idle' ? 'idle' : 'waking';
  }
  if (instance === 'running') return inferred === 'waking' ? 'working' : inferred;
  return inferred;
}

export function buildJobBoard(jobs: PipelineJob[], now: number, instance: PipelineInstanceState = 'unknown'): JobBoard {
  const views = jobs.map(job => buildJobView(job, now));

  const running = views.filter(view => view.job.status === 'running');
  const queued = views.filter(view => view.job.status === 'pending');
  const history = views
    .filter(view => !['running', 'pending'].includes(view.job.status))
    .sort((a, b) => timeOf(b.job.finishedAt ?? b.job.createdAt) - timeOf(a.job.finishedAt ?? a.job.createdAt));

  return {
    // Four states. "A queue with work and nothing running means the box is booting" was true
    // until a job could be *held* — waiting for a model to answer, on a timer, with the machine
    // switched off. Reported as "Encendiendo" that reads as a machine about to do something,
    // which is the opposite of what is happening and the exact confusion this state model exists
    // to prevent.
    machine: machineStateFrom(instance, machineStateOf(running, queued, now)),
    running: running.sort((a, b) => timeOf(a.job.startedAt) - timeOf(b.job.startedAt)),
    queued: queued.sort((a, b) => timeOf(a.job.createdAt) - timeOf(b.job.createdAt)),
    history,
    throughput: buildThroughput(views, now),
  };
}

/** A job whose `notBefore` is still in the future is waiting on purpose, not on the machine. */
function isHeld(view: JobView, now: number): boolean {
  const notBefore = timeOf(view.job.notBefore);
  return notBefore > now;
}

function machineStateOf(running: JobView[], queued: JobView[], now: number): MachineState {
  if (running.length > 0) return 'working';
  if (queued.length === 0) return 'idle';
  // Every queued job waiting out a timer means nothing is going to start until one expires.
  return queued.every(view => isHeld(view, now)) ? 'blocked' : 'waking';
}

export function buildJobView(job: PipelineJob, now: number): JobView {
  const started = timeOf(job.startedAt);
  const finished = timeOf(job.finishedAt);
  const created = timeOf(job.createdAt);

  return {
    job,
    progress: buildProgress(job),
    runningMs: started === 0 ? null : (finished || now) - started,
    waitingMs: started === 0 ? Math.max(0, now - created) : null,
    retryable: isRetryable(job),
  };
}

/**
 * The stages this run asked for, each with what happened to it.
 *
 * `abandoned` is its own standing: a run that failed at the claims stage did not merely stop
 * before the prenota, it will never reach it, and drawing those as "pending" leaves an editor
 * waiting for work nobody is going to do.
 */
function buildProgress(job: PipelineJob): JobProgress {
  const asked = job.stages.filter((stage): stage is PipelineStage => PIPELINE_STAGES.includes(stage));
  const done = new Set(job.stagesDone);
  const settled = job.status === 'failed' || job.status === 'cancelled';

  const track = asked.map(stage => {
    if (done.has(stage)) return { stage, standing: 'done' as const };
    if (stage === job.currentStage && job.status === 'running') return { stage, standing: 'running' as const };
    return { stage, standing: settled ? ('abandoned' as const) : ('pending' as const) };
  });

  const doneCount = track.filter(entry => entry.standing === 'done').length;

  return {
    track,
    done: doneCount,
    total: asked.length,
    percent: asked.length === 0 ? 0 : Math.round((100 * doneCount) / asked.length),
    current: (asked.find(stage => stage === job.currentStage) ?? null) as PipelineStage | null,
  };
}

/**
 * Whether asking again is likely to help.
 *
 * Transcription failures on this pipeline are dominated by two transient causes — the download and
 * the provider rate limit — and both clear on their own. A refusal about the request itself does
 * not, and offering a retry there teaches an editor that the button does nothing.
 */
const TRANSIENT_ERROR = /timeout|timed out|rate limit|429|503|502|connection|econnreset|temporarily|unavailable|ffmpeg|download|Empty LLM response/i;

function isRetryable(job: PipelineJob): boolean {
  if (job.status !== 'failed') return false;
  if (!job.error) return true; // a failure with no message at all is a crash, and crashes retry
  return TRANSIENT_ERROR.test(job.error);
}

function buildThroughput(views: JobView[], now: number): JobBoard['throughput'] {
  const dayAgo = now - 24 * 60 * 60 * 1000;
  const settledToday = views.filter(view => {
    const finished = timeOf(view.job.finishedAt);
    return finished > 0 && finished >= dayAgo;
  });

  const durations = settledToday
    .filter(view => view.job.status === 'done')
    .map(view => view.runningMs ?? 0)
    .filter(ms => ms > 0)
    .sort((a, b) => a - b);

  return {
    finishedToday: settledToday.filter(view => view.job.status === 'done').length,
    failedToday: settledToday.filter(view => view.job.status === 'failed').length,
    // The median rather than the mean: one three-hour mañanera beside four ten-minute clips makes
    // the mean describe a run nobody has ever waited for.
    medianMs: durations.length > 0 ? (durations[Math.floor((durations.length - 1) / 2)] ?? null) : null,
  };
}

function timeOf(value: string | null | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** `2 h 14 min`, `9 min`, `48 s`. Long runs are the norm here, so hours come first. */
export function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}
